/**
 * NEXORA AI - SelectionCard Component (Design System)
 * Epic 1.3 - Onboarding Premium
 * 
 * Composant réutilisable pour toutes les sélections dans l'application
 * Utilisé dans : Onboarding (Region, Language, Goals, Preferences), Assistants IA, Formulaires, Workflows
 * 
 * API Extensible :
 * - Sélection simple ou multiple
 * - États : default, selected, disabled, recommended
 * - Icône/Emoji/Image optionnels
 * - Badge/Tag optionnel
 * - Description optionnelle
 * - Animations configurables
 */

import React from 'react';
import { Check, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './card';
import { cn } from '@/lib/utils';

/**
 * SelectionCard Props
 * 
 * @param {string} id - Identifiant unique de la card
 * @param {boolean} selected - État sélectionné
 * @param {function} onSelect - Callback de sélection
 * @param {boolean} disabled - État désactivé
 * @param {boolean} recommended - Badge "Recommandé" (futures évolutions)
 * @param {string} icon - Emoji ou classe d'icône
 * @param {ReactNode} iconComponent - Composant d'icône Lucide (optionnel)
 * @param {string} title - Titre principal
 * @param {string} description - Description secondaire (optionnel)
 * @param {string} badge - Badge/Tag personnalisé (optionnel)
 * @param {string} benefit - Bénéfice affiché à la sélection (optionnel)
 * @param {string} size - Taille de la card : 'sm' | 'md' | 'lg'
 * @param {string} variant - Variante visuelle : 'default' | 'outline' | 'gradient'
 * @param {boolean} showCheckmark - Afficher le checkmark animé
 * @param {boolean} animateOnHover - Activer animation hover
 * @param {object} gradientColors - Couleurs gradient personnalisées
 * @param {string} className - Classes Tailwind supplémentaires
 */
export default function SelectionCard({
  id,
  selected = false,
  onSelect,
  disabled = false,
  recommended = false,
  icon = null,
  iconComponent: IconComponent = null,
  title,
  description = null,
  badge = null,
  benefit = null,
  size = 'md',
  variant = 'default',
  showCheckmark = true,
  animateOnHover = true,
  gradientColors = null,
  className = '',
  children = null,
  testId = null,
}) {
  // Tailles
  const sizeClasses = {
    sm: 'p-3 sm:p-4 min-h-[60px]',
    md: 'p-5 sm:p-6 min-h-[88px] sm:min-h-[100px]',
    lg: 'p-6 sm:p-8 min-h-[120px] sm:min-h-[140px]',
  };

  // Variantes
  const variantClasses = {
    default: selected
      ? `border-2 ${gradientColors?.border || 'border-blue-500/50'} bg-gradient-to-br ${gradientColors?.bg || 'from-blue-500/10 to-purple-500/10'}`
      : 'border border-zinc-800 bg-zinc-900/30 hover:border-zinc-700 hover:bg-zinc-900/50',
    outline: selected
      ? 'border-2 border-blue-500 bg-blue-500/5'
      : 'border-2 border-zinc-800 hover:border-zinc-600',
    gradient: selected
      ? 'border-2 border-transparent bg-gradient-to-br from-blue-600 to-purple-600'
      : 'border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-zinc-800 hover:to-zinc-700',
  };

  // Gestion du clic
  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect(id);
    }
  };

  return (
    <motion.div
      whileTap={!disabled && animateOnHover ? { scale: 0.98 } : {}}
      data-testid={testId || `selection-card-${id}`}
    >
      <Card
        onClick={handleClick}
        className={cn(
          'relative cursor-pointer transition-all duration-200',
          sizeClasses[size],
          variantClasses[variant],
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && animateOnHover && 'hover:shadow-lg',
          className
        )}
      >
        {/* Badge Recommandé (coin supérieur droit) */}
        {recommended && !selected && (
          <motion.div
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            className="absolute -top-2 -right-2 bg-gradient-to-br from-yellow-500 to-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-lg flex items-center gap-1"
          >
            <Star className="w-3 h-3 fill-current" />
            <span>Recommandé</span>
          </motion.div>
        )}

        {/* Checkmark animé (coin supérieur droit) */}
        <AnimatePresence>
          {selected && showCheckmark && (
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="absolute top-3 right-3 w-7 h-7 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg z-10"
            >
              <Check className="w-4 h-4 text-white" strokeWidth={3} />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-start gap-3 sm:gap-4">
          {/* Icône/Emoji */}
          {(icon || IconComponent) && (
            <motion.div
              animate={selected ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0"
            >
              {icon && (
                <div className="text-3xl sm:text-4xl">
                  {icon}
                </div>
              )}
              {IconComponent && (
                <div className={cn(
                  "w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center",
                  selected 
                    ? "bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30" 
                    : "bg-zinc-800/50 border border-zinc-700"
                )}>
                  <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-zinc-300" strokeWidth={1.5} />
                </div>
              )}
            </motion.div>
          )}

          {/* Contenu */}
          <div className="flex-1 min-w-0 pr-8">
            {/* Titre + Badge */}
            <div className="flex items-center gap-2 mb-1">
              <h3 className={cn(
                "font-semibold text-white",
                size === 'sm' && "text-sm",
                size === 'md' && "text-sm sm:text-base",
                size === 'lg' && "text-base sm:text-lg"
              )}>
                {title}
              </h3>
              {badge && (
                <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium">
                  {badge}
                </span>
              )}
            </div>

            {/* Description */}
            {description && (
              <p className={cn(
                "text-zinc-400 mb-2",
                size === 'sm' && "text-xs",
                size === 'md' && "text-xs sm:text-sm",
                size === 'lg' && "text-sm sm:text-base"
              )}>
                {description}
              </p>
            )}

            {/* Bénéfice (révélé à la sélection) */}
            <AnimatePresence>
              {selected && benefit && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1.5 text-xs text-blue-400 mt-2"
                >
                  <Check className="w-3 h-3 flex-shrink-0" />
                  <span className="font-medium">{benefit}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Children personnalisés */}
            {children}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

/**
 * SelectionCardGrid - Wrapper pour grilles responsives
 * 
 * @param {ReactNode} children - SelectionCard components
 * @param {number} cols - Nombre de colonnes : 1, 2, 3, 4
 * @param {string} className - Classes supplémentaires
 */
export function SelectionCardGrid({ children, cols = 2, className = '' }) {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={cn(
      'grid gap-3 sm:gap-4',
      gridClasses[cols],
      className
    )}>
      {children}
    </div>
  );
}
